<template>
  <el-card v-loading="loading">
    <el-header slot="header">
      <el-page-header @back="$router.back()" :content="`${user.name || user.email}'s Thresholds`" />
    </el-header>

    <el-main>
      <section>
        <el-row>
          <el-col :span="24">
            <h4>Threshold Limits</h4>

            <el-table
              :data="thresholds"
              style="width: 100%">
              <el-table-column
                label="Type">
                <template slot-scope="scope">
                  <el-select 
                    v-model="thresholds[scope.$index].type" 
                    placeholder="Threshold Type"
                    :disabled="isSaving">
                    <el-option
                      v-for="item in thresholdOptionsFiltered"
                      :key="item.value"
                      :label="item.label"
                      :value="item.value">
                    </el-option>
                  </el-select>
                </template>
              </el-table-column>
              <el-table-column
                label="Limit">
                <template slot-scope="scope">
                  <el-input-number 
                  @change="thresholds.splice(scope.$index, 1, {...scope.row, isDirty: true})"
                  :min="0"
                  :controls="false"
                  v-model="thresholds[scope.$index].limit"
                  :disabled="isSaving" />
                </template>
              </el-table-column>
              <el-table-column
                align="right">
                <template slot="header" slot-scope="scope">
                  <el-button 
                    plain
                    icon="el-icon-plus"
                    type="success"
                    size="mini" 
                    @click="handleAddThreshold"
                    :disabled="isSaving">
                    Add Threshold
                  </el-button>
                </template>
                <template slot-scope="scope">
                  <el-popconfirm 
                    :title="`Are you sure to delete this threshold?`"
                    confirmButtonText="Delete"
                    cancelButtonText="Cancel"
                    icon="el-icon-info"
                    iconColor="red"
                    @onConfirm="handleRemoveThreshold(scope.row, scope.$index)">
                    <el-button
                      plain
                      slot="reference"
                      size="mini"
                      icon="el-icon-delete"
                      :disabled="isSaving" />
                  </el-popconfirm>
                </template>
              </el-table-column>
            </el-table>
          </el-col>
        </el-row>
      </section>
    </el-main>

    <el-footer>
      <el-button type="primary" :disabled="!isSavable" :loading="isSaving" @click="save">Save</el-button>
    </el-footer>
  </el-card>
</template>

<script>
  import axios, { RepositoryError } from '../../repositories';
  import { notify } from '../../utils';

  export default {
    name: 'ManageUserThresholds',
    async mounted() {
      this.loading = true;
      await Promise.all([
        this.fetch(),
        this.fetchThresholds(),
      ]);
      this.loading = false;
    },
    props: {
      user_id: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        user: {
          name: '',
          email: '',
        },
        loading: false,
        isSaving: false,
        thresholds: [],
        thresholdOptions: [
          {value: 'coupon_fixed', label: 'Coupon Fixed'},
          {value: 'coupon_percentage', label: 'Coupon Percentage'},
        ],
      };
    },
    computed: {
      thresholdOptionsFiltered() {
        return this.thresholdOptions.filter(
          option => !this.thresholds.find(threshold => threshold.type === option.value)
        );
      },
      isSavable() {
        return this.thresholds.filter(threshold => threshold.isNew || threshold.isDirty).length > 0;
      },
    },
    methods: {
      async fetch() {
        try {
          const response = await axios.get(`/users/${this.user_id}`);

          this.user = response.data;
          this.user_roles = response.data.roles.map(role => role.name);
          this.user_permissions = response.data.permissions;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      async fetchThresholds() {
        try {
          const response = await axios.get(`/auth/user-thresholds/${this.user_id}`);

          this.thresholds = response.data;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      async save() {
        try {
          this.isSaving = true;

          await Promise.all(
            this.thresholds.filter(threshold => threshold.isDirty)
              .map((threshold, index) => this.updateThreshold(threshold, index))
          );

          this.thresholds = this.thresholds.map(threshold => ({
            ...threshold,
            isDirty: false,
            isNew: false,
          }));
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        } finally {
          this.isSaving = false;
        }
      },
      async updateThreshold(threshold, index) {
        try {
          await axios.put(`/auth/user-thresholds/${this.user_id}/threshold/${threshold.type}`, {
            limit: threshold.limit,
          });

          this.thresholds[index].isDirty = false;
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
      handleAddThreshold() {
        this.thresholds = [...this.thresholds, {
          type: '',
          limit: 0,
          isNew: true,
          isDirty: true,
        }];
      },
      async handleRemoveThreshold(threshold, index) {
        try {
          if (!threshold.isNew) {
            await axios.delete(`/auth/user-thresholds/${this.user_id}/threshold/${threshold.type}`);
          }

          this.thresholds.splice(index, 1);
        } catch (err) {
          if (err instanceof RepositoryError) {
            return;
          }

          throw err;
        }
      },
    },
  }
</script>
